import { z } from 'zod';
import { type ItemCategory, itemCategorySchema } from '../inventory/definitions';
import { isSovereignJewel, jewelFitsSocket } from '../inventory/jewels';
import { RARITIES, type Rarity, raritySchema } from '../rarity';

/**
 * **A carga de campo: propriedade que ainda não é propriedade.**
 *
 * ## O que este ficheiro decide, e o que deliberadamente não decide
 *
 * Decide o que a carga de campo **é**: a forma de um item que está a voar num
 * quadrante perigoso, quem pode entrar nela, e a fronteira fechada que responde
 * "isto pode cair?". Não decide o que cai, quanto cai, nem de onde — não há uma
 * única recompensa neste gate, e inventar uma aqui seria decidir conteúdo por
 * acidente.
 *
 * ## Porque a carga de campo NÃO é um `item_instances`
 *
 * Porque `item_instances` é o cofre, e um item no cofre **pertence a uma
 * conta**: a coluna `owner_account_id` é `not null`, e é isso que a torna a
 * representação certa para propriedade segura e a representação errada para
 * isto.
 *
 * Um item de campo tem dois estados que o cofre não sabe exprimir:
 *
 * - **está numa caixa**, e não é de ninguém. Não há conta a pôr na coluna, e
 *   pôr a do morto seria dizer que ele ainda o tem;
 * - **está num casco que pode morrer**, e portanto não é seu de forma
 *   permanente. Um item que aparecesse no cofre no instante em que caiu seria
 *   propriedade segura antes da extração, que é exatamente a coisa que este
 *   gate não pode criar.
 *
 * Por isso a carga de campo **referencia** os contratos canónicos — o id de
 * definição de `item_definitions`, a raridade de `rarity.ts`, a categoria de
 * `inventory/definitions.ts` — e **não duplica catálogo nenhum**. Não há uma
 * segunda lista de equipamentos, de artefactos, de joias ou de cartas aqui, e
 * não pode haver: a única coisa que este ficheiro sabe sobre um item é o id que
 * o catálogo real já lhe deu.
 *
 * A extração — um gate futuro — é o que transforma um item de campo numa linha
 * de `item_instances`. Até lá ele existe, tem identidade, e não é de ninguém em
 * sentido económico.
 */

/* -------------------------------------------------------------------------- */
/*  Versões                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * A versão da carga de campo. **A dela**, e de mais nada.
 *
 * Não é a versão do snapshot da sala, nem a do protocolo, nem a do handoff:
 * documentos com tempos de vida diferentes, e uma mudança num deles nunca pode
 * obrigar a mexer nos outros.
 */
export const OPEN_WORLD_FIELD_CARGO_VERSION = 1;

/** A versão da caixa de morte. A dela: uma caixa vive quinze minutos, uma carga vive uma expedição. */
export const OPEN_WORLD_DEATH_BOX_VERSION = 1;

/**
 * A versão dos contratos económicos do mundo aberto — concessões e saques.
 *
 * Ela versiona as **mensagens** entre a sala e a autoridade que move
 * propriedade, exatamente como `OPEN_WORLD_HANDOFF_VERSION` versiona as
 * mensagens entre a sala e a autoridade que move localização.
 */
export const OPEN_WORLD_FIELD_ECONOMY_VERSION = 1;

/* -------------------------------------------------------------------------- */
/*  A capacidade                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Os espaços que qualquer casco tem, antes de um único ponto de vida extra.
 *
 * A fórmula inteira vive em `game-domain`
 * (`open-world/field/capacity.ts`), porque precisa da vida-base canónica e
 * essa constante vive lá. Este é o único número dela que também é útil de fora:
 * um cliente que mostre "0 / 5" antes de qualquer coisa acontecer.
 */
export const OPEN_WORLD_BASE_CARGO_SLOTS = 5;

/**
 * O maior número de espaços que uma linha de base de dados aceita.
 *
 * **Não é um teto de jogo.** A fórmula não tem teto: dez mil de vida dão cento e
 * quatro espaços e um milhão daria dez mil e quatro, e nada nas regras os
 * limita. Isto é o limite de sanidade da coluna — um valor acima dele não
 * descreve um casco, descreve um erro de cálculo — e está posto tão alto que
 * nenhuma vida que o jogo possa compor lhe chega perto.
 */
export const OPEN_WORLD_MAX_CARGO_SLOTS = 1_000_000;

/* -------------------------------------------------------------------------- */
/*  O que um item de campo é                                                  */
/* -------------------------------------------------------------------------- */

/**
 * As duas naturezas que um item de campo pode ter.
 *
 * - `definition` — tem uma entrada no catálogo real. Um equipamento, um
 *   artefacto, uma joia de socket, uma carta. O id **é** o de
 *   `item_definitions`, e a definição continua a viver lá.
 * - `creature` — um peixe ou um alien. Não tem entrada no catálogo porque **não
 *   existe catálogo de espécies**, e este gate não o inventa. O que ele tem é
 *   espécie, raridade e um peso individual, que é o que faz duas capturas da
 *   mesma espécie serem duas coisas diferentes.
 */
export const OPEN_WORLD_FIELD_ITEM_KINDS = ['definition', 'creature'] as const;
export type OpenWorldFieldItemKind = (typeof OPEN_WORLD_FIELD_ITEM_KINDS)[number];

/** O maior peso que uma captura pode ter, em miligramas. Dez toneladas. */
export const OPEN_WORLD_MAX_CREATURE_WEIGHT_MG = 10_000_000_000;

/** Quantas propriedades congeladas um item pode carregar, e que tamanho cada uma tem. */
export const OPEN_WORLD_FIELD_PROPERTY_LIMIT = 16;
export const OPEN_WORLD_FIELD_PROPERTY_KEY_MAX = 48;
export const OPEN_WORLD_FIELD_PROPERTY_VALUE_MAX = 256;

/**
 * **Um item de campo, e a identidade que ele nunca perde.**
 *
 * `fieldItemId` acompanha-o do jogador para a caixa e da caixa para outro
 * jogador. Não é regenerado em nenhum desses passos, e é por isso que "o item
 * que o assassino levou é o item que a vítima tinha" é uma comparação e não uma
 * inferência.
 *
 * Tudo o resto é **congelado na concessão**. A raridade, o nível e as
 * propriedades são o que o item era quando entrou no mundo, e nada os
 * recalcula: um item roubado que voltasse a ler o catálogo mudaria de valor
 * porque o catálogo mudou, e o que a vítima perdeu não é o que o ladrão ganhou.
 *
 * **Nada empilha.** Cada item ocupa exatamente um espaço e tem o seu próprio
 * `fieldItemId`, o que faz "capturas com pesos diferentes não empilham" ser uma
 * consequência da forma em vez de uma regra que alguém tem de lembrar.
 */
export interface OpenWorldFieldItem {
  readonly v: typeof OPEN_WORLD_FIELD_CARGO_VERSION;
  /** A identidade. Sobrevive a jogador -> caixa -> jogador, e nunca é recriada. */
  readonly fieldItemId: string;
  readonly kind: OpenWorldFieldItemKind;
  /** A categoria canónica. `null` para uma captura, que não tem uma. */
  readonly category: ItemCategory | null;
  /** O id de `item_definitions`. `null` para uma captura. */
  readonly definitionId: string | null;
  readonly rarity: Rarity;
  /** Congelado. Um item roubado mantém o nível que tinha. */
  readonly level: number;
  /** Só uma captura. A espécie, como o gate que criar a pesca a nomear. */
  readonly speciesId: string | null;
  /** Só uma captura. Miligramas inteiros: duas capturas da mesma espécie pesam diferente. */
  readonly weightMilligrams: number | null;
  /**
   * O que mais este item precisa de levar consigo, congelado na concessão.
   *
   * Strings, e só strings: um número aqui seria um número em JSON, e um número
   * em JSON é a forma de perder um dígito sem que nada o diga. Quem precisar de
   * uma magnitude escreve-a em decimal canónico e lê-a de volta em `bigint`.
   */
  readonly properties: Record<string, string>;
}

/**
 * **O resumo de uma carga, como ele viaja.**
 *
 * Três números e uma revisão, e é deliberadamente tudo: os espaços congelados,
 * quantos estão ocupados e quanto ouro de campo há. **Nunca a lista de itens.**
 *
 * É isto que acompanha o piloto numa travessia e é isto que uma sala espelha.
 * Serializar o conteúdo em cada handoff seria duplicar propriedade numa
 * mensagem — e uma mensagem duplicada é uma propriedade duplicada no dia em que
 * uma delas for reentregue. A referência é a conta e o mundo; a autoridade tem
 * a lista, e é a única que a tem.
 *
 * A revisão é o que torna uma cópia atrasada detetável em vez de acreditada.
 */
export interface OpenWorldFieldCargoSummary {
  readonly v: typeof OPEN_WORLD_FIELD_CARGO_VERSION;
  /** Congelados na criação da expedição. Nada os recalcula do outro lado. */
  readonly slots: number;
  readonly used: number;
  /** Decimal canónico: um inteiro exato na autoridade, uma string em todo o lado. */
  readonly gold: string;
  readonly revision: number;
}

export const openWorldFieldCargoSummarySchema = z
  .object({
    v: z.literal(OPEN_WORLD_FIELD_CARGO_VERSION),
    slots: z.number().int().min(0).max(OPEN_WORLD_MAX_CARGO_SLOTS),
    used: z.number().int().min(0).max(OPEN_WORLD_MAX_CARGO_SLOTS),
    gold: z
      .string()
      .regex(/^(0|[1-9][0-9]*)$/)
      .max(40),
    revision: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  })
  .strict()
  .refine((value) => value.used <= value.slots, {
    message: 'a cargo cannot hold more than it has room for',
  });

const fieldItemIdSchema = z.uuid();

export const openWorldFieldItemSchema = z
  .object({
    v: z.literal(OPEN_WORLD_FIELD_CARGO_VERSION),
    fieldItemId: fieldItemIdSchema,
    kind: z.enum(OPEN_WORLD_FIELD_ITEM_KINDS),
    category: itemCategorySchema.nullable(),
    definitionId: z.string().min(1).max(128).nullable(),
    rarity: raritySchema,
    level: z.number().int().min(1).max(1_000_000),
    speciesId: z.string().min(1).max(128).nullable(),
    weightMilligrams: z.number().int().min(1).max(OPEN_WORLD_MAX_CREATURE_WEIGHT_MG).nullable(),
    properties: z.record(
      z.string().min(1).max(OPEN_WORLD_FIELD_PROPERTY_KEY_MAX),
      z.string().max(OPEN_WORLD_FIELD_PROPERTY_VALUE_MAX),
    ),
  })
  .strict()
  .refine(
    (value) =>
      value.kind === 'definition'
        ? value.definitionId !== null &&
          value.category !== null &&
          value.speciesId === null &&
          value.weightMilligrams === null
        : value.definitionId === null &&
          value.category === null &&
          value.speciesId !== null &&
          value.weightMilligrams !== null,
    {
      message: 'a field item is a catalogue definition or a creature, never both and never neither',
    },
  )
  .refine((value) => Object.keys(value.properties).length <= OPEN_WORLD_FIELD_PROPERTY_LIMIT, {
    message: 'a field item carries a bounded number of frozen properties',
  });

/* -------------------------------------------------------------------------- */
/*  A fronteira de elegibilidade                                              */
/* -------------------------------------------------------------------------- */

/**
 * **Porque isto não pode cair.** Uma lista fechada, e é a única.
 *
 * Uma resposta por razão, e nenhuma delas é "não sei": a fronteira é **total**
 * sobre as categorias que o catálogo tem, o que quer dizer que uma categoria
 * nova adicionada a `ITEM_CATEGORIES` **não compila** aqui sem alguém decidir
 * de que lado ela cai. É esse o ponto: a lista de coisas que podem ser roubadas
 * cresce por decisão e nunca por omissão.
 */
export const OPEN_WORLD_CARGO_REFUSALS = [
  /** Ouro e Cash não são itens. Não existe definição para eles, e não pode existir. */
  'currency',
  /**
   * Uma skin é a aparência de uma conta, não espólio de campo.
   *
   * **Já não é recusada** desde o V28I.J.1: uma cópia de skin tem capacidade de
   * sockets própria, e selá-la tornou-a transportável como qualquer outra. O
   * código fica pela mesma razão que o de cima — registos antigos usaram-no.
   */
  'skin',
  /** Comprado com Cash, ou concedido por uma compra. Dinheiro real não cai no chão. */
  'premium',
  /**
   * Único por conta. O Coração Primordial é o caso que existe hoje: incorporado,
   * sem socket e sem nível, e uma conta tem os que tem. Roubá-lo seria mover
   * algo que não é uma cópia de nada.
   */
  'account_unique',
  /** Item de missão. Uma missão que perdesse a sua peça no chão deixaria de ser uma missão. */
  'quest',
  /** Um id que o catálogo real não conhece. Recusado, nunca aproximado. */
  'unknown_definition',
  /**
   * Uma categoria que a carga de campo não transporta.
   *
   * **Nenhuma, hoje.** Naves, canhões, pets, consumíveis e skins estiveram aqui
   * até ao gate V28I.J.1, que os tirou por autorização explícita depois de a
   * representação passar a saber selar o que cada um precisa.
   *
   * O código fica: ele existe em registos escritos antes, e uma categoria nova
   * que ninguém classifique continua a cair nele pelo ramo exaustivo — que é o
   * que impede uma categoria futura de entrar na carga por omissão.
   */
  'unsupported_category',
] as const;
export type OpenWorldCargoRefusal = (typeof OPEN_WORLD_CARGO_REFUSALS)[number];

/**
 * O que quem concede tem de saber sobre um item para a fronteira poder responder.
 *
 * As duas últimas vêm da **proveniência** da concessão e não do catálogo, pela
 * mesma razão que `item_instances.soulbound` vem: são factos sobre esta cópia,
 * e o catálogo descreve o tipo.
 */
export interface OpenWorldCargoSubject {
  readonly kind: OpenWorldFieldItemKind;
  /** A categoria canónica. Ignorada para uma captura, que não tem uma. */
  readonly category: ItemCategory | null;
  /** O id de `item_definitions`, quando é um item de catálogo. */
  readonly definitionId: string | null;
  /** Se o catálogo real conhece este id. Perguntado a ele, nunca adivinhado aqui. */
  readonly definitionExists: boolean;
  /** Comprado com Cash, ou concedido por uma compra. Da proveniência da concessão. */
  readonly premium: boolean;
  /** Único por conta por uma regra que não é a do socket. Da proveniência. */
  readonly accountUnique: boolean;
  /** Peça de missão. Da proveniência. */
  readonly quest: boolean;
}

export type OpenWorldCargoEligibility =
  | { readonly ok: true }
  | { readonly ok: false; readonly refusal: OpenWorldCargoRefusal };

const ALLOWED: OpenWorldCargoEligibility = Object.freeze({ ok: true });

const refuse = (refusal: OpenWorldCargoRefusal): OpenWorldCargoEligibility =>
  Object.freeze({ ok: false, refusal });

/**
 * **Pode isto entrar na carga de campo?**
 *
 * A única resposta a essa pergunta em todo o projeto. Uma segunda cópia dela
 * escrita noutro sítio é como um item passa a ser legal para a concessão e
 * ilegal para o saque — ou, pior, o contrário.
 *
 * Fechada e total: cada categoria do catálogo é nomeada, e o `switch` sem
 * `default` faz de uma categoria nova um erro de compilação em vez de um item
 * que ninguém decidiu deixar cair.
 */
export function openWorldCargoEligibility(
  subject: OpenWorldCargoSubject,
): OpenWorldCargoEligibility {
  if (subject.premium) {
    return refuse('premium');
  }
  if (subject.quest) {
    return refuse('quest');
  }
  if (subject.accountUnique) {
    return refuse('account_unique');
  }
  if (subject.kind === 'creature') {
    /*
     * Uma captura não passa pelo catálogo porque não há catálogo de espécies.
     * O que a torna legal é ser uma captura, e a forma já garantiu que ela
     * traz espécie, raridade e peso.
     */
    return ALLOWED;
  }
  if (subject.definitionId === null || !subject.definitionExists || subject.category === null) {
    return refuse('unknown_definition');
  }
  const category: ItemCategory = subject.category;
  switch (category) {
    case 'equipment':
    case 'artifact':
    case 'card':
      return ALLOWED;
    case 'jewel':
      /*
       * Só a de socket. A soberana é incorporada — não ocupa socket, não tem
       * nível e não é uma cópia de nada — e a distinção pertence a `jewels.ts`,
       * que é quem a sabe. Comparar o id à mão aqui seria uma segunda resposta.
       */
      if (isSovereignJewel(subject.definitionId)) {
        return refuse('account_unique');
      }
      return jewelFitsSocket(subject.definitionId) ? ALLOWED : refuse('unknown_definition');
    /*
     * **As cinco que entraram no gate V28I.J.1.**
     *
     * Naves, canhões, pets, consumíveis e skins estavam recusados, e a lista de
     * recusas dizia por escrito que isso era *"uma decisão e não um
     * esquecimento: o gate que os quiser fazer cair tem de os tirar desta lista
     * deliberadamente"*. É este o gate, e a autorização foi dada.
     *
     * O que os fazia difíceis não era a legalidade — era a REPRESENTAÇÃO: uma
     * skin tem capacidade de sockets sorteada, um consumível vive numa pilha e
     * não numa instância. As duas coisas passaram a ser seláveis em
     * `field-reward.ts`, e é por isso que a recusa deixou de ter razão de ser.
     *
     * O que continua fora está fora por outros motivos, e nenhum é
     * conveniência: moeda não é item, uma captura pertence à pesca, a joia
     * soberana é única por conta, e um item pago, de missão ou desconhecido é
     * recusado pelos ramos acima.
     */
    case 'skin':
    case 'ship':
    case 'cannon':
    case 'pet':
    case 'consumable':
      return ALLOWED;
    default: {
      /*
       * Exaustividade provada pelo compilador. Uma categoria nova em
       * `ITEM_CATEGORIES` chega aqui como um `never` que não é atribuível, e o
       * gate que a criar tem de decidir de que lado dela cai.
       */
      const exhaustive: never = category;
      void exhaustive;
      return refuse('unsupported_category');
    }
  }
}

/* -------------------------------------------------------------------------- */
/*  A identidade de uma morte                                                 */
/* -------------------------------------------------------------------------- */

/** O separador. Nenhum uuid o contém, portanto duas mortes não podem colidir. */
const DEATH_ID_SEPARATOR = '|';

/**
 * **O id de uma morte, derivado e nunca cunhado.**
 *
 * Uma morte tem de ser processada **exatamente uma vez**, e o processamento
 * atravessa uma fronteira que pode falhar: a sala pede, a autoridade responde,
 * e a resposta pode perder-se. Um id aleatório obrigaria a sala a escrevê-lo
 * antes de perguntar e a lembrá-lo através de uma evicção; um id **derivado** é
 * o mesmo id em cada tentativa, porque é uma função do que já aconteceu.
 *
 * As cinco partes, e porque cada uma:
 *
 * - **o mundo**, porque uma conta pode existir em dois;
 * - **a coordenada**, porque duas salas contam os seus próprios tiques;
 * - **a conta**, porque a morte é dela;
 * - **o tique da destruição**, porque é o instante;
 * - **a ordinal de entrada**, porque uma conta que saiu e voltou é outra vida,
 *   e a ordinal só sobe.
 *
 * Repetir o pedido com o mesmo id não cria uma segunda caixa. É essa a única
 * coisa que este id existe para garantir.
 */
export function openWorldDeathId(input: {
  readonly worldId: string;
  readonly rowIndex: number;
  readonly columnIndex: number;
  readonly accountId: string;
  readonly destroyedAtTick: number;
  readonly spawnOrdinal: number;
}): string {
  return [
    input.worldId,
    String(input.rowIndex),
    String(input.columnIndex),
    input.accountId,
    String(input.destroyedAtTick),
    String(input.spawnOrdinal),
  ].join(DEATH_ID_SEPARATOR);
}

/**
 * **O quadrante onde uma morte aconteceu, lido do próprio id** (gate V28I.BY).
 *
 * A recuperação de uma expedição fechada por uma morte tem de escrever o
 * relatório dela no quadrante certo, e o id já o diz: é derivado dele. `null`
 * para um id que não tem a forma.
 */
export function openWorldDeathCoordinateOf(
  deathId: string,
): { readonly rowIndex: number; readonly columnIndex: number } | null {
  const parts = deathId.split(DEATH_ID_SEPARATOR);
  if (parts.length !== 6) {
    return null;
  }
  const rowIndex = Number(parts[1]);
  const columnIndex = Number(parts[2]);
  if (!Number.isSafeInteger(rowIndex) || !Number.isSafeInteger(columnIndex)) {
    return null;
  }
  if (rowIndex < 0 || columnIndex < 0) {
    return null;
  }
  return { rowIndex, columnIndex };
}

/** Um id de morte como ele viaja. Comprimento limitado, e nada mais é assumido. */
export const openWorldDeathIdSchema = z.string().min(1).max(256);

/* -------------------------------------------------------------------------- */
/*  A ordem canónica                                                          */
/* -------------------------------------------------------------------------- */

/**
 * **A ordem em que `PEGAR TUDO` pega.**
 *
 * Existe porque a caixa pode não caber toda, e "quais é que entraram" tem de ser
 * a mesma resposta em qualquer máquina e em qualquer tentativa. A ordem em que
 * uma consulta devolveu linhas não serve: é incidental, e este projeto não
 * deriva nada de uma ordem incidental.
 *
 * Raridade primeiro — porque, se só três entram, o jogador quer os três que
 * valem mais, e não os três que a caixa aconteceu a listar primeiro — depois o
 * nível, e por fim o `fieldItemId`, que desempata sempre porque é único.
 */
export function openWorldFieldItemOrder(
  left: OpenWorldFieldItem,
  right: OpenWorldFieldItem,
): number {
  /*
   * A escala é a do jogo, lida de `RARITIES` e nunca copiada: uma segunda lista
   * escrita aqui envelheceria em silêncio no dia em que a escala mudasse.
   */
  const rarity = RARITIES.indexOf(right.rarity) - RARITIES.indexOf(left.rarity);
  if (rarity !== 0) {
    return rarity;
  }
  if (left.level !== right.level) {
    return right.level - left.level;
  }
  return left.fieldItemId < right.fieldItemId ? -1 : left.fieldItemId > right.fieldItemId ? 1 : 0;
}
